import React from 'react';
import shopperPayoutService from '../../services/shopperPayoutService';

/**
 * Progress screen shared by the validation and the processing stage.
 * Validation counts checked rows, processing counts rows already paid out.
 */
const ImportProgress = ({
  title,
  subtitle,
  progressLabel,
  ratio = 0,
  onCancel,
  cancelLabel = 'Cancel',
  cancelDisabled = false,
}) => {
  const percent = Math.round(Math.min(Math.max(ratio, 0), 1) * 100);

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8'>
      <h2 className='text-2xl font-bold text-gray-900'>{title}</h2>
      {subtitle && <p className='mt-1 text-sm text-gray-600'>{subtitle}</p>}

      <div className='mt-6 flex items-center gap-4'>
        <div
          className='flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden'
          role='progressbar'
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={title}
        >
          <div
            className='h-full bg-blue-600 rounded-full transition-all duration-300'
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className='text-sm font-bold text-gray-900 w-12 text-right'>
          {percent}%
        </span>
      </div>

      {progressLabel && (
        <p className='mt-2 text-sm text-blue-600'>{progressLabel}</p>
      )}

      <div className='mt-6 flex items-start gap-3 bg-blue-50 rounded-lg px-4 py-3'>
        <svg
          className='w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
          />
        </svg>
        <p className='text-sm text-blue-700'>
          Please stay on this page until the process is complete.
        </p>
      </div>

      {onCancel && (
        <div className='mt-6'>
          <button
            type='button'
            onClick={onCancel}
            disabled={cancelDisabled}
            className='border border-gray-300 text-gray-700 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {cancelLabel}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Build the "900/1,250 shopper payouts checked" line for the current stage.
 * @param {Object} batch
 * @param {boolean} isProcessing
 * @returns {string}
 */
export const buildProgressLabel = (batch, isProcessing) => {
  if (!batch) return '';

  const done = isProcessing
    ? (batch.successRows || 0) + (batch.failedRows || 0)
    : batch.checkedRows || 0;
  const total = isProcessing ? batch.validRows || 0 : batch.totalRows || 0;
  const verb = isProcessing ? 'processed' : 'checked';

  return `${shopperPayoutService.formatCount(
    done
  )}/${shopperPayoutService.formatCount(total)} shopper payouts ${verb}`;
};

export default ImportProgress;
